import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import styled from "styled-components";

import { Field } from "../../Field";
import { IconButton } from "../../IconButton";
import { TextInput } from "../../Input";
import { controlFocusRing, controlTransition } from "../../foundations/controlStyles";
import { toMinutes } from "./timeUtils";
import type { TimeValue } from "./types";

type Segment = "hours" | "minutes";

/** 편집 중 draft — 각 칸을 문자열로 들고 있어 타이핑(비패딩)·리스트 선택(패딩)을 함께 다룬다. */
interface TimeDraft {
  hours: string;
  minutes: string;
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const parseSeg = (s: string): number | null => (s === "" ? null : Number.parseInt(s, 10));
const toDraft = (value: TimeValue | null): TimeDraft =>
  value ? { hours: pad2(value.hours), minutes: pad2(value.minutes) } : { hours: "", minutes: "" };
const sameTime = (a: TimeValue | null, b: TimeValue | null): boolean =>
  a === b || (!!a && !!b && a.hours === b.hours && a.minutes === b.minutes);

export interface TimeFieldsProps {
  /** controlled 입력값. null = 미완성/미선택. */
  value: TimeValue | null;
  /** 두 칸이 모두 유효하면 `{ hours, minutes }`, 아니면 null을 emit. */
  onChange: (value: TimeValue | null) => void;
  /** 선택 가능 하한(포함) — 옵션 disable 판정. */
  min?: TimeValue;
  /** 선택 가능 상한(포함). */
  max?: TimeValue;
  /** 분 스텝(기본 1). 리스트 옵션 간격. */
  minuteStep?: number;
  /** 빈 칸에 보여줄 placeholder 시각(예: 지금). 없으면 "HH"/"MM". */
  placeholderTime?: TimeValue;
  /** Minute 오른쪽에 현재 시각으로 채우는 "Now" 버튼 노출(기본 false). */
  showNow?: boolean;
  /** "Now" 버튼 라벨(기본 "Now"). */
  nowLabel?: ReactNode;
  /**
   * 24:00 **입력** 허용(RFC-datetime-range-half-open). true면 Hour 0–24(hour=24면 minute 0 고정). 24:00은 "오늘의 끝"
   * 입력 편의 — 실제 다음날 00:00으로의 정규화·표시는 컨테이너(DateTimePicker) 책임. 기본 false.
   * (TimeInput은 날짜가 없어 rollover 불가 → 0–23 유지.)
   */
  allow24?: boolean;
}

/**
 * 시각 입력 presentational(RFC-time-input §추출 · RFC-date-time-picker). 시/분 **2자리 타이핑 + 각 칸 아래 인라인
 * 리스트 콤보박스**(세로 컬럼, date 그리드와 구분). controlled — value/onChange로 잇고
 * min/max/step로 옵션을 disable한다. **정규화(스냅)·안내문·팝업·트리거는 컨테이너
 * (TimeInput/DateTimePicker) 책임**. TimeInput·DateTimePicker가 이 조각을 공유한다(Calendar가
 * DatePicker류에 공유되는 것과 동형 패턴).
 */
export const TimeFields = ({
  value,
  onChange,
  min,
  max,
  minuteStep = 1,
  placeholderTime,
  showNow = false,
  nowLabel = "Now",
  allow24 = false,
}: TimeFieldsProps) => {
  const listboxId = useId();
  const [draft, setDraft] = useState<TimeDraft>(() => toDraft(value));
  const [listSegment, setListSegment] = useState<Segment | null>(null);
  // 마지막으로 emit한 값 — 외부 value 변화가 "자기 emit"인지 구분해 draft 재시드를 결정한다.
  const lastEmitted = useRef<TimeValue | null>(value);

  // 외부에서 value가 (자기 emit이 아닌) 다른 값으로 바뀌면 draft 재시드(예: 컨테이너 OK 스냅).
  useEffect(() => {
    if (!sameTime(value, lastEmitted.current)) {
      setDraft(toDraft(value));
      lastEmitted.current = value;
    }
  }, [value]);

  // hour=24("오늘의 끝", allow24 전용)면 minute은 0으로 고정.
  const enforce24 = (d: TimeDraft): TimeDraft =>
    parseSeg(d.hours) === 24 ? { ...d, minutes: "00" } : d;

  const emit = (d: TimeDraft) => {
    const h = parseSeg(d.hours);
    const m = h === 24 ? 0 : parseSeg(d.minutes);
    const v = h != null && m != null ? { hours: h, minutes: m } : null;
    lastEmitted.current = v;
    onChange(v);
  };

  const hourOptions = useMemo(
    () => Array.from({ length: allow24 ? 25 : 24 }, (_, h) => h),
    [allow24],
  );
  const minuteOptions = useMemo(() => {
    const step = minuteStep > 1 ? minuteStep : 1;
    const out: number[] = [];
    for (let m = 0; m < 60; m += step) out.push(m);
    return out;
  }, [minuteStep]);

  const hoursNum = parseSeg(draft.hours);
  const minutesNum = parseSeg(draft.minutes);

  // 리스트 열릴 때 선택 항목을 가운데로 스크롤.
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!listSegment) return;
    const list = listRef.current;
    const sel = selectedRef.current;
    if (!list || !sel) return;
    // rect 기반(offsetParent 무관): 선택 항목을 리스트 가운데로. 첫 항목이면 음수→0으로 clamp되어 맨 위.
    const listRect = list.getBoundingClientRect();
    const selRect = sel.getBoundingClientRect();
    list.scrollTop += selRect.top - listRect.top - list.clientHeight / 2 + selRect.height / 2;
  }, [listSegment]);

  // min/max로 아예 고를 수 없는 옵션은 disable. 시(hour)는 그 시의 어떤 분으로도 범위 안에 못 들면,
  // 분(minute)은 현재 입력된 시 기준으로 판정(시 미입력이면 판정 불가라 활성).
  const isOptionDisabled = (seg: Segment, n: number): boolean => {
    if (seg === "hours") {
      if (min && n * 60 + 59 < toMinutes(min)) return true;
      if (max && n * 60 > toMinutes(max)) return true;
      return false;
    }
    if (hoursNum == null) return false;
    if (hoursNum === 24) return n !== 0; // 24:00은 분 0만.
    const t = hoursNum * 60 + n;
    if (min && t < toMinutes(min)) return true;
    if (max && t > toMinutes(max)) return true;
    return false;
  };

  const setSeg = (seg: Segment, text: string) => {
    const nd = enforce24({ ...draft, [seg]: text });
    setDraft(nd);
    emit(nd);
  };
  const padSeg = (seg: Segment) => {
    const nd = { ...draft, [seg]: draft[seg] === "" ? "" : draft[seg].padStart(2, "0") };
    setDraft(nd);
    emit(nd);
  };
  const pickOption = (seg: Segment, n: number) => {
    const nd = enforce24({ ...draft, [seg]: pad2(n) });
    setDraft(nd);
    setListSegment(null);
    emit(nd);
  };
  // Now 버튼 — 이 컨트롤의 시각을 현재로 채운다(정규화는 컨테이너 OK에서 — step/min·max 스냅).
  const applyNow = () => {
    const d = new Date();
    const nd = { hours: pad2(d.getHours()), minutes: pad2(d.getMinutes()) };
    setDraft(nd);
    setListSegment(null);
    emit(nd);
  };

  const hintFor = (seg: Segment): string => {
    if (!placeholderTime) return seg === "hours" ? "HH" : "MM";
    return pad2(seg === "hours" ? placeholderTime.hours : placeholderTime.minutes);
  };

  // 해당 필드 아래에 뜨는 옵션 리스트(세로 컬럼, 입력 폭과 동일). 선택 항목은 열릴 때 가운데로 스크롤.
  const renderList = (seg: Segment) => {
    const options = seg === "hours" ? hourOptions : minuteOptions;
    const selected = seg === "hours" ? hoursNum : minutesNum;
    return (
      <SegList
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-label={seg === "hours" ? "Hour" : "Minute"}
      >
        {options.map((n) => {
          const isSel = n === selected;
          return (
            <SegOption
              key={n}
              ref={isSel ? selectedRef : undefined}
              type="button"
              role="option"
              aria-selected={isSel}
              $selected={isSel}
              disabled={isOptionDisabled(seg, n)}
              // 옵션 클릭 시 입력 blur를 막아(포커스 유지) 리스트가 즉시 닫히지 않게 한다.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pickOption(seg, n)}
            >
              {pad2(n)}
            </SegOption>
          );
        })}
      </SegList>
    );
  };

  const segment = (seg: Segment, ariaLabel: string) => {
    // 입력 검증: 2자리 숫자로 제한하고, 각 칸의 유효 상한을 넘으면 상한으로 clamp
    // (Hour 0–23, Minute 0–59). 리딩 제로는 유지(패딩은 blur에서), 62분·25시 같은 무효 입력 방지.
    const segMax = seg === "hours" ? (allow24 ? 24 : 23) : 59;
    const sanitize = (v: string) => {
      const digits = v.replace(/\D/g, "").slice(0, 2);
      if (digits === "") return "";
      return Number(digits) > segMax ? String(segMax) : digits;
    };
    return (
      <Field label={ariaLabel} layout="stack">
        <TextInput
          aria-label={ariaLabel}
          inputMode="numeric"
          role="combobox"
          aria-expanded={listSegment === seg}
          aria-controls={listSegment === seg ? listboxId : undefined}
          placeholder={hintFor(seg)}
          value={draft[seg]}
          sanitize={sanitize}
          onChange={(event) => setSeg(seg, event.target.value)}
          onFocus={() => setListSegment(seg)}
          onClick={() => setListSegment(seg)}
          onBlur={() => padSeg(seg)}
        />
      </Field>
    );
  };

  return (
    <>
      <TimeRow>
        <TimeCol>{segment("hours", "Hour")}</TimeCol>
        <Colon aria-hidden="true">:</Colon>
        <TimeCol>{segment("minutes", "Minute")}</TimeCol>
        {showNow ? (
          <NowSlot>
            <IconButton variant="surface" text={nowLabel} onClick={applyNow} />
          </NowSlot>
        ) : null}
      </TimeRow>

      {/* 리스트는 해당 필드(시/분) 아래 컬럼에 정렬해 띄운다(같은 컬럼 폭·간격 미러링). */}
      {listSegment ? (
        <ListRow>
          <TimeCol>{listSegment === "hours" ? renderList("hours") : null}</TimeCol>
          <ColonSpacer aria-hidden="true" />
          <TimeCol>{listSegment === "minutes" ? renderList("minutes") : null}</TimeCol>
        </ListRow>
      ) : null}
    </>
  );
};

const TimeRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`;

// 리스트 행 — TimeRow와 같은 컬럼 폭·간격을 미러링해 시/분 리스트가 각 필드 아래 정렬되게 한다.
const ListRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const TimeCol = styled.div`
  width: 4.5rem;
`;

// Now 버튼 — Minute 오른쪽. 라벨 없는 버튼이라 flex-end로 입력 라인에 맞춰 정렬된다.
const NowSlot = styled.div`
  display: flex;
`;

const COLON_WIDTH = "0.75rem";

const Colon = styled.span`
  width: ${COLON_WIDTH};
  text-align: center;
  padding-bottom: 0.375rem;
  font-size: ${({ theme }) => theme.typography.size.title};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  color: ${({ theme }) => theme.color.text.default};
`;

// Colon과 동일 폭 — ListRow에서 시/분 리스트 컬럼을 TimeRow와 정렬.
const ColonSpacer = styled.span`
  width: ${COLON_WIDTH};
`;

// 시각 팝업 안에 인라인으로 펼치는 옵션 리스트(중첩 팝오버 회피). date의 월/연 그리드와 혼동되지
// 않도록 **세로 단일 컬럼**(select 목록 형태).
// §1 예외(사용자 결정): 짧고 범위가 자명한 옵션 리스트라 스크롤바를 시각적으로 숨긴다(스크롤 동작은 유지).
// 그래서 c6 `yScrollbarStyles` 대신 scrollbar 숨김 처리.
const SegList = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  max-height: 12rem;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* 구 Edge/IE */
  &::-webkit-scrollbar {
    display: none; /* WebKit */
  }
`;

const SegOption = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 입력 md와 높이 일치: 동일 padding + 1px border box(투명) + body 폰트. */
  padding: 0.5rem 0.6rem;
  border: 1px solid transparent;
  line-height: 1;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  color: ${({ theme, $selected }) =>
    $selected ? theme.color.text.onControl : theme.color.text.default};
  background: ${({ theme, $selected }) =>
    $selected ? theme.color.bg.control.primary : "transparent"};
  cursor: pointer;
  ${controlTransition("background-color", "color")}

  &:not(:disabled):not([aria-selected="true"]):hover {
    background: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  }
  &:focus-visible {
    ${controlFocusRing()}
  }
  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
`;
